import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { Item, ItemContext, ItemResourcePayload } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

import {
  ADD_ITEM,
  INCREMENT_ITEM_GROUP_ITEMS_QUERY_INDEX,
  LOADED_ALL_ITEM_GROUP_ITEMS,
  RESET_ITEM_GROUP_ITEMS_QUERY_INDEX,
  UPDATE_ITEM_GROUP_ITEMS,
  UPDATE_ITEM_GROUP_ITEMS_TOTAL,
  UPDATE_ITEMS
} from './mutations'

import { ADD_ITEM_PORTIONS } from '../portions/mutations'

const actions: ActionTree<MenuState, RootState> = {
  /**
   * Adds a new Item and updates an ItemGroup relation.
   *
   * @param item Item to be added.
   * @param itemGroupId ItemGroup id in which the Item will be added.
   */
  addItem (
    { commit, state },
    { item, itemGroupId }: { item: Item; itemGroupId: number }
  ) {
    const itemGroupItems = state.itemGroups.items[itemGroupId]

    if (itemGroupItems === undefined) {
      throw new Error(`Items relation for ItemGroup "${itemGroupId}" was not found`)
    }

    commit(ADD_ITEM_PORTIONS, item.id)

    commit(ADD_ITEM, item)

    commit(UPDATE_ITEM_GROUP_ITEMS_TOTAL, {
      itemGroupId,
      total: itemGroupItems.total + 1
    })

    commit(UPDATE_ITEM_GROUP_ITEMS, {
      itemGroupId,
      items: [...itemGroupItems.ids, item.id]
    })
  },

  /**
   * Creates an Item.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the Item.
   */
  async createItem ({ dispatch, rootState, rootGetters }, payload: ItemResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const item = await api.menu.items.createItem(tokenV2, providerId, businessUnit.id, payload)

    await dispatch('addItem', { item, itemGroupId: payload.groupId })

    return item
  },

  /**
   * Loads one Item.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the ItemGroup if it is not loaded.
   *
   * @param context ItemContext to be loaded.
   */
  async loadItem (
    { commit, dispatch, rootState, rootGetters, state },
    { id, itemGroupId }: ItemContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    let itemGroupItems = state.itemGroups.items[itemGroupId]

    if (itemGroupItems === undefined) {
      await dispatch('loadItemGroup', itemGroupId)
      itemGroupItems = state.itemGroups.items[itemGroupId]
    }

    const item = await api.menu.items.fetchItem(tokenV2, providerId, businessUnit.id, id)

    if (!itemGroupItems.ids.has(item.id)) {
      await dispatch('addItem', { item, itemGroupId })
    } else {
      const items = mergeById(state.items.elements, [item])
      commit(UPDATE_ITEMS, items)
    }
  },

  /**
   * Loads a page of Items.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the ItemGroup if it is not loaded.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   *
   * @param itemGroupId ItemGroup id whose Items will be loaded.
   */
  async loadItemPage (
    { commit, dispatch, rootState, rootGetters, state },
    itemGroupId: number
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    let itemGroupItems = state.itemGroups.items[itemGroupId]

    if (itemGroupItems === undefined) {
      await dispatch('loadItemGroup', itemGroupId)
      itemGroupItems = state.itemGroups.items[itemGroupId]
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.items.fetchItemPage(
      tokenV2,
      providerId,
      businessUnit.id,
      {
        ...itemGroupItems.query,
        groupId: itemGroupId
      }
    )

    if (itemGroupItems.total !== page.total) {
      commit(UPDATE_ITEM_GROUP_ITEMS_TOTAL, { itemGroupId, total: page.total })
    }

    if (page.elements.length > 0) {
      for (const item of page.elements) {
        if (state.items.portions[item.id] === undefined) {
          commit(ADD_ITEM_PORTIONS, item.id)
        }
      }

      const items = mergeById(state.items.elements, page.elements)

      commit(UPDATE_ITEMS, items)

      const ids = [
        ...itemGroupItems.ids,
        ...page.elements.map((i) => i.id)
      ]

      commit(UPDATE_ITEM_GROUP_ITEMS, { itemGroupId, items: ids })

      if ([...new Set(ids)].length === page.total) {
        commit(LOADED_ALL_ITEM_GROUP_ITEMS, itemGroupId)
      }

      commit(INCREMENT_ITEM_GROUP_ITEMS_QUERY_INDEX, {
        itemGroupId,
        offset: page.elements.length
      })
    } else {
      commit(RESET_ITEM_GROUP_ITEMS_QUERY_INDEX, itemGroupId)

      if (page.total === 0 && itemGroupItems.ids.size > 0) {
        commit(UPDATE_ITEM_GROUP_ITEMS, { itemGroupId, items: [] })
      }
    }
  },

  /**
   * Updates an Item.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Item id to be updated.
   * @param payload Payload to update it.
   */
  async updateItem (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: ItemResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const item = await api.menu.items.updateItem(tokenV2, providerId, businessUnit.id, id, payload)

    const items = mergeById(state.items.elements, [item])

    commit(UPDATE_ITEMS, items)
  },

  /**
   * Updates an Item image.
   *
   * It rejects if the user is not logged in.
   *
   * @param id Item id whose image will be updated.
   * @param imageFile File to be uploaded.
   */
  async updateItemImage (
    { commit, rootState, rootGetters, state },
    { id, imageFile }: {id: number; imageFile: File}
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const formData = new FormData()

    formData.append('file', imageFile)

    const item = await api.menu.items.updateItemImage(tokenV2, providerId, businessUnit.id, id, formData)

    const items = mergeById(state.items.elements, [item])

    commit(UPDATE_ITEMS, items)
  }
}

export default actions
